import { FetchResult, useMutation } from '@apollo/client';
import CircularProgress from 'components/CircularProgress';
import { CancelButton, ConfirmButton } from 'components/common/DefaultFormButton';
import { INPUT_TYPE } from 'components/common/Input/types';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { InputProps } from 'components/InputFieldsGenerator/types';
import { Form, Formik } from 'formik';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import ROUTES from 'routes/routeConstants';
import { CREATE_TRANSACTION, UPDATE_TRANSACTION } from 'services/graphql/mutations/transactions';
import { GeneralFunction } from 'types/common';
import { ILocation } from 'types/location';
import { ITransactionType } from 'types/services';
import { IAddress, ITransaction } from 'types/transaction';
import { defaultNotification, formatCurrency, getLink } from 'utils';
import * as Yup from 'yup';

import { generateTransactionTypeOptions } from './helpers';

const CommonTransactionForm: React.FC<{
  closeForm: any;
  onSubmit?: GeneralFunction;
  transaction?: ITransaction;
  transactionTypes?: ITransactionType[];
  selectedTransactionType?: ITransactionType;
  callbacks?: { onSuccess?: GeneralFunction; onError?: GeneralFunction; finally?: GeneralFunction };
  locations?: ILocation[];
  loading?: boolean;
  fetchNextPageLocationData?: GeneralFunction;
}> = ({
  closeForm,
  onSubmit,
  transaction,
  callbacks,
  transactionTypes,
  selectedTransactionType,
  locations,
  loading,
}) => {
  const { mainLocation } = useCurrentLoggedInUser(); // test nha
  const [selectedTransactionTypeState, setSelectedTransactionTypeState] = useState<ITransactionType>();
  const history = useHistory();

  const transactionSchemaObject = useMemo(
    () => ({
      reference: Yup.string().required('Required'),
      description: Yup.string().required('Required'),
      transactionType: Yup.string().required('Required'),
    }),
    [],
  );

  const getTransactionType = (transactionTypeId: string) => {
    let selectedTransactionTypeTemp: ITransactionType | undefined;
    if (transactionTypes) {
      transactionTypes.some((transactionType) => {
        if (transactionType.id === transactionTypeId) {
          selectedTransactionTypeTemp = transactionType;
          return true;
        }
        return false;
      });
    }
    return selectedTransactionTypeTemp;
  };

  useEffect(() => {
    if (selectedTransactionType) setSelectedTransactionTypeState(selectedTransactionType);
    else if (transaction) {
      setSelectedTransactionTypeState(getTransactionType(transaction.transactionType.id));
    }
  }, [selectedTransactionType, transaction]);

  const [createTransaction, { loading: createTransactionLoading }] = useMutation(CREATE_TRANSACTION);
  const [updateTransaction, { loading: updateTransactionLoading }] = useMutation(UPDATE_TRANSACTION);

  const transactionTypesDataSelectOptions = generateTransactionTypeOptions(transactionTypes || []);

  const transactionFormFields: InputProps[] = [
    {
      label: 'Notary to Upload Signed Documents',
      name: 'requiredScanDoc',
      type: INPUT_TYPE.SWITCH,
    },
    {
      label: 'Reference Number',
      name: 'reference',
    },
    {
      label: 'Description',
      name: 'description',
    },
    {
      label: 'Transaction Type',
      name: 'transactionType',
      type: INPUT_TYPE.SELECT,
      inputOptions: transactionTypesDataSelectOptions || [],
      onChange: (v: string) => {
        setSelectedTransactionTypeState(getTransactionType(v));
      },
    },
  ];

  const transactionSchema = useMemo(() => Yup.object(transactionSchemaObject), []);

  const formInitValues = useMemo(
    () => ({
      reference: transaction?.reference || '',
      description: transaction?.description || '',
      transactionType: transaction?.transactionType?.id || '',
      location: (transaction ? transaction?.location?.id : mainLocation?.id) || '',
      requiredScanDoc: transaction?.requiredScanDoc || false,
    }),
    [transaction],
  );

  const onSubmitForm = async (submitValues: {
    reference: string;
    description: string;
    transactionType: string;
    location: string;
    returnShippingAddress?: IAddress;
    requiredScanDoc: boolean;
  }) => {
    const values = { ...submitValues };
    values.returnShippingAddress = locations?.find((loc) => loc.id === values.location)?.address || undefined;
    if (onSubmit) {
      onSubmit(values);
      return;
    }

    let response: FetchResult<{ createTransactionEntity: ITransaction }> = {};
    try {
      if (transaction) {
        await updateTransaction({
          variables: {
            filter: { id: transaction.id },
            updatedDoc: values,
          },
        });
      } else {
        response = await createTransaction({
          variables: {
            doc: values,
          },
        });
      }
      closeForm();
      callbacks?.onSuccess?.();
      defaultNotification({ success: `${transaction ? 'Update' : 'Create'} Transaction successfully!` });
      if (!transaction && response?.data?.createTransactionEntity?.id) {
        history.push(getLink(ROUTES.TRANSACTION_DETAIL, { id: response?.data.createTransactionEntity.id }));
      }
    } catch (err) {
      defaultNotification({ failure: 'Something went wrong! Please try again.' });
      callbacks?.onError?.();
    }
    callbacks?.finally?.();
  };

  return (
    <>
      {loading ? (
        <CircularProgress />
      ) : (
        <Formik
          initialValues={formInitValues}
          name='basic'
          onSubmit={onSubmitForm}
          validationSchema={transactionSchema}
          enableReinitialize
          validateOnBlur>
          <Form className='gx-signin-form gx-form-row0'>
            <InputFieldsGenerator formFields={transactionFormFields} />
            <div style={{ paddingLeft: 16, marginBottom: 12 }}>
              <span className='gx-font-weight-medium'>Office Location</span>
              <div style={{ marginTop: 10 }}>
                {mainLocation ? (
                  <span>{mainLocation.name}</span>
                ) : (
                  <span style={{ color: 'red' }}>Please set main location first</span>
                )}
              </div>
            </div>
            <div style={{ paddingLeft: 16, marginBottom: 12 }}>
              <span className='gx-font-weight-medium'>
                Price per Appointment: {formatCurrency(selectedTransactionTypeState?.userPrice?.priceInPerson || 0)}
              </span>
            </div>
            <div style={{ paddingLeft: 16 }}>
              <ConfirmButton
                disabled={!mainLocation}
                isLoading={createTransactionLoading || updateTransactionLoading}
                className='gx-mb-0 gx-mt-2'
                htmlType='submit'>
                {transaction ? 'Save' : 'Create'}
              </ConfirmButton>
              <CancelButton type='default' className='gx-mb-0 gx-mt-2' onClick={closeForm}>
                Cancel
              </CancelButton>
            </div>
          </Form>
        </Formik>
      )}
    </>
  );
};

export default CommonTransactionForm;
